import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormHelperText,
    ListItemButton,
    SxProps,
    TextField,
    Theme,
    Typography
} from '@mui/material';
import t from '../theme';
import { addIconStyle, cancelButtonStyle, saveButtonStyle } from '../style';

//Icons import
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { FormEvent, useState } from 'react';
import { StyledDialogActions } from '../styledComponents';

interface AddConfigureItemProps {
    sectionName: string;
    onNewItemAdd: (object: GenerateRequestObject | null) => void;
}

export type GenerateRequestObject = {
    name: string;
    section: string;
    value: string | null;
    type: string;
    isEditable: boolean;
    isToggable: boolean;
    isActive: boolean;
};

const AddItemWideButton = ({ sectionName, onNewItemAdd }: AddConfigureItemProps) => {
    const [isDialogOpened, setDialogOpened] = useState(false);
    const [showErrorHelperText, setErrorHeleperText] = useState(false);
    const [value, setValue] = useState('');

    const handleCloseDialog = () => {
        setDialogOpened(false);
        setErrorHeleperText(false);
    };

    function generateRequestObject(
        sectionName: string,
        value: string
    ): GenerateRequestObject | null {
        switch (sectionName) {
            case Sections.WhatDidYouDo: {
                const result: GenerateRequestObject = {
                    name: Sections.WhatDidYouDo,
                    section: 'Ancillary Hours',
                    value: value,
                    type: 'String',
                    isEditable: true,
                    isToggable: true,
                    isActive: true
                };
                return result;
            }
            case Sections.HowLongDidItTake: {
                const result: GenerateRequestObject = {
                    name: Sections.HowLongDidItTake,
                    section: 'Ancillary Hours',
                    value: !Number.isNaN(parseFloat(value)) ? value : null,
                    type: 'Number',
                    isEditable: true,
                    isToggable: true,
                    isActive: true
                };
                return result;
            }
            default:
                return null;
        }
    }

    const handleSave = async (event: FormEvent) => {
        event.preventDefault();
        const object = generateRequestObject(sectionName, value);

        // const onlyLettersExpression = new RegExp('/^[A-Za-z/s]*$/');
        if (sectionName === Sections.WhatDidYouDo) {
            if (/^[A-Za-z\s`!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>/?~]*$/.test(value)) {
                onNewItemAdd(object);
                setDialogOpened(false);
            } else {
                setErrorHeleperText(true);
            }
        } else {
            onNewItemAdd(object);
            setDialogOpened(false);
        }
    };

    return (
        <>
            <ListItemButton sx={listItemButtonStyles} onClick={() => setDialogOpened(true)}>
                <Box
                    sx={{
                        display: 'flex',
                        margin: 'auto',
                        minHeight: '2.5rem',
                        alignItems: 'center'
                    }}>
                    <AddCircleOutlineIcon sx={addIconStyle} />
                    <Typography variant="h4" sx={{ color: t.palette.primary.light }}>
                        Add Item
                    </Typography>
                </Box>
            </ListItemButton>
            <Dialog open={isDialogOpened} onClose={handleCloseDialog}>
                <DialogTitle variant="h3">{`Add new item for "${sectionName}"`}</DialogTitle>
                <form onSubmit={handleSave}>
                    <DialogContent>
                        <TextField
                            required
                            margin="dense"
                            id="name"
                            label="Value"
                            type="text"
                            fullWidth
                            variant="outlined"
                            onChange={(v) => {
                                setValue(v.target.value);
                                setErrorHeleperText(false);
                            }}
                        />
                        {showErrorHelperText ? (
                            <FormHelperText error={showErrorHelperText}>
                                {sectionName === Sections.WhatDidYouDo
                                    ? 'Only letters are allowed.'
                                    : ''}
                            </FormHelperText>
                        ) : null}
                    </DialogContent>
                    <StyledDialogActions>
                        <Button sx={cancelButtonStyle} onClick={handleCloseDialog}>
                            Cancel
                        </Button>
                        <Button sx={saveButtonStyle} type="submit">
                            Add
                        </Button>
                    </StyledDialogActions>
                </form>
            </Dialog>
        </>
    );
};

const listItemButtonStyles: SxProps<Theme> = (t: Theme) => ({
    backgroundColor: t.palette.listItemButton.main,
    color: t.palette.primary.light,
    '&:hover': {
        backgroundColor: t.palette.listItemButton.hoverMain,
        '& .MuiListItemIcon-root': {
            color: t.palette.primary.light
        }
    }
});

export default AddItemWideButton;

export const Sections = {
    WhatDidYouDo: 'What did you work on?',
    HowLongDidItTake: 'How long did it take?'
};
